import React, { useCallback } from 'react';
import Text from 'xpi-black-ui/Base/Typography/Text';
import NumberBadge from '../../Utils/NumberBadge';
import GenericInput from '../../Generics/GenericInput';
import WrapperDialog, {
  WrapperHeader,
  WrapperDialogContent,
  WrapperDialogContentSeparator,
  WrapperDialogContentLegs,
  WrapperDialogContentInformation,
  WrapperPositionInformation,
  WrapperSection,
  WrapperLegInformation,
  WrapperLegInfo,
} from './styles';

export default function UnwindingProposalDialog({ onClose, proposal, isOpen }) {

  const PositionInformation = useCallback(({ title, value, isFullWidth }) => (
    <WrapperPositionInformation isFullWidth={isFullWidth}>
      <GenericInput title={title} defaultValue={`${value}`} isReadOnly />
    </WrapperPositionInformation>
  ), []);

  const LegInformation = useCallback(({ index, info }) => (
    <WrapperLegInformation>
      <NumberBadge value={index + 1} />
      <WrapperLegInfo>
        <Text bigger>{info.option}</Text>
        {info.strike && <Text bigger>{info.strike}</Text>}
        {info.barrier && <Text bigger>{info.barrier}</Text>}
      </WrapperLegInfo>
    </WrapperLegInformation>
  ), []);

  const LegFixedInformation = useCallback(({ index, value }) => (
    <WrapperLegInformation>
      <NumberBadge value={index} />
      <WrapperLegInfo>
        <Text bigger>{value}</Text>
      </WrapperLegInfo>
    </WrapperLegInformation>
  ), []);

  const getLegInformation = useCallback(({ barrierType, barrierValue, optionType, side, strikeValue, quantity }) => {
    const IS_SPOT = optionType.toUpperCase() === 'SPOT';
    const STRIKE_TITLE = IS_SPOT ? '' : `Strike ${strikeValue}`;
    const BARRIER_TITLE = barrierType ? `Barreira ${barrierType} ${barrierValue}` : '';

    return {
      option: `${side} ${optionType} - Qtde: ${quantity}`,
      strike: STRIKE_TITLE,
      barrier: BARRIER_TITLE
    }
  }, []);

  const handleOnClose = useCallback(() => {
    onClose();
  }, []);

  return (
    <WrapperDialog overlayClick={handleOnClose} open={isOpen}>
      <WrapperHeader onClose={handleOnClose} title={`Proposta Antecipação (#${proposal?.unwindTicketId})`} />

      <WrapperDialogContent>
        <WrapperDialogContentSeparator>
          <WrapperSection>
            <WrapperDialogContentLegs>
              {proposal?.legs?.map(({
                barrierType,
                barrierValue,
                optionType,
                side,
                strikeValue,
                quantity,
              }, index) => (
                <LegInformation
                  key={`${optionType}-${side}`}
                  index={index}
                  info={getLegInformation({
                    side,
                    optionType,
                    barrierType,
                    barrierValue,
                    strikeValue,
                    quantity
                  })}
                />
              ))}
              <LegFixedInformation key='fixing' index={proposal?.legs?.length + 1} value={`Data de Fixing - ${proposal?.fixingDate}`} />
            </WrapperDialogContentLegs>

            <WrapperDialogContentInformation>
              <PositionInformation title="Cliente" value={proposal?.clientId} />
              <PositionInformation title="Ativo" value={proposal?.asset.ticker} />

              <PositionInformation title="Estrutura" value={proposal?.structureName} />
              <PositionInformation title="Quantidade" value={proposal?.quantity} />

              <PositionInformation title="Assessor" value={proposal?.advisorCode} />
              <PositionInformation title="Data de Envio" value={proposal?.proposalDate} />

              <PositionInformation title="Preço Spot de Cotação" value={proposal?.asset.indicativePrice} />
              <PositionInformation title="Comissão Indicativa" value={proposal?.indicativeComission} />

              {proposal?.reason !== 'Cancelado' && (
                <>
                  <PositionInformation title="Preço Executado" value={proposal?.asset.executedPrice} />
                  <PositionInformation title="Comissão Final Gerada" value={proposal?.finalComission} />
                </>
              )}

              <PositionInformation title="Com Venda do Ativo" value={(proposal?.withSellAsset) ? 'Sim' : 'Não'} />
              <PositionInformation title="Status" value={proposal?.status} />

              {proposal?.reason && (<PositionInformation title="Motivo" value={proposal?.reason} isFullWidth />)}
              {proposal?.status !== 'Cancelado' && (
                <>
                  {proposal?.withSellAsset ?
                    (
                      <>
                        <PositionInformation title="Valor na Entrada (Ações + Opções)" value={proposal?.acquisitionCost} isFullWidth />
                        <PositionInformation title="Financeiro Final (Ação)" value={proposal?.finalPosition.assetValue} />
                        <PositionInformation title="Financeiro Final (Opções)" value={proposal?.finalPosition.optionValue} />
                      </>
                    ) :
                    (
                      <>
                        <PositionInformation title="Valor na Entrada (Opções)" value={proposal?.acquisitionCost} isFullWidth />
                        <PositionInformation title="Financeiro Final (Opções)" value={proposal?.finalPosition.optionValue} isFullWidth />
                      </>
                    )
                  }

                  <PositionInformation title="Resultado Bruto Final" value={proposal?.finalPosition.profitValue} isFullWidth />
                </>
              )}

            </WrapperDialogContentInformation>
          </WrapperSection>

        </WrapperDialogContentSeparator>
      </WrapperDialogContent>
    </WrapperDialog>
  );
}
